import React, { useEffect, useRef, useState, memo } from 'react';
import './index.less';
import { Modal, Tooltip, App } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import globalStore from '@/store/global';
import { areKeysPresentAndNonEmpty } from '@/utils';
import { getPracticeSetting, savePracticeSetting } from '@/apis';
import queryDomMethods from '@/utils/queryDomMethods';

const PracticeSetting: React.FC = () => {
    const [initInfo, setInitInfo] = useState<{ projectId: number; subjectId: number }>();
    const { message } = App.useApp();
    const practiceQuestionNum = useRef([5, 10, 15, 20, 30, 50, 80, 100]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const practiceSettingRef = useRef<any>({});
    const [settings, setSettings] = useState({ practiceMode: 0, practiceNum: 0, yearType: 0 });

    const handleCancelModal = () => setIsModalOpen(false);

    const handleSubmit = async () => {
        const response = await savePracticeSetting({ ...practiceSettingRef.current, ...settings });
        if (response.status === 0) {
            handleCancelModal();
        } else {
            message.error(response.message);
        }
    };

    const fetchData = async () => {
        const response = await getPracticeSetting(initInfo!);

        if (response.status === 0 && response.result) {
            practiceSettingRef.current = response.result;
            setSettings({
                practiceMode: response.result.practiceMode,
                practiceNum: response.result.practiceNum,
                yearType: response.result.yearType,
            });
        }
    };

    useEffect(() => {
        if (isModalOpen) {
            fetchData();
        }
    }, [isModalOpen]);

    useEffect(() => {
        globalStore.subscribe('PracticeSetting', ({ type, params }) => {
            switch (type) {
                case 'open':
                    if (areKeysPresentAndNonEmpty(params, ['projectId', 'subjectId'])) {
                        setInitInfo(params);
                        setIsModalOpen(true);
                    } else {
                        console.error('Invalid params for PracticeSetting');
                    }
                    break;
                default:
                    setInitInfo(undefined);
                    break;
            }
        });
        return () => {
            globalStore.unsubscribe('PracticeSetting');
        };
    }, []);

    const renderSettingOption = (label: string, param: keyof typeof settings, value: number) => (
        <div
            className={classNames('sub-tiku__practice-setting-checkbox', { active: settings[param] === value })}
            onClick={() => setSettings(prev => ({ ...prev, [param]: value }))}
        >
            {label}
        </div>
    );

    return (
        <Modal
            className="sub-tiku__practice-setting-modal"
            footer={
                <div style={{ textAlign: 'center' }}>
                    <button className="sub-tiku__tiku-default-btn" onClick={handleCancelModal}>
                        <span>取消</span>
                    </button>
                    <button className="sub-tiku__tiku-default-btn sub-tiku__tiku-primary-btn" onClick={handleSubmit}>
                        <span>保存</span>
                    </button>
                </div>
            }
            onCancel={handleCancelModal}
            title="练习设置"
            open={isModalOpen}
            destroyOnClose
            width={640}
            getContainer={queryDomMethods.querySubTikuRoot}
        >
            <div className="sub-tiku__practice-setting-title">
                设置刷题模式
                <Tooltip
                    placement="right"
                    title={
                        <>
                            <p>练习模式：交卷后查看解析</p>
                            <p>背题模式：做一题看一题解析</p>
                        </>
                    }
                >
                    <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.65)', marginLeft: 8 }} />
                </Tooltip>
            </div>
            <div className="sub-tiku__practice-setting-type">
                {renderSettingOption('练习模式', 'practiceMode', 0)}
                {renderSettingOption('背题模式', 'practiceMode', 1)}
            </div>

            <div className="sub-tiku__practice-setting-title">请选择出题年份</div>
            <div className="sub-tiku__practice-setting-type">
                {renderSettingOption('不限', 'yearType', 0)}
                {renderSettingOption('近3年', 'yearType', 1)}
                {renderSettingOption('近5年', 'yearType', 2)}
                {renderSettingOption('近10年', 'yearType', 3)}
            </div>

            <div className="sub-tiku__practice-setting-title">请选择每组练习题数量（上次未做完的练习不受此影响）</div>
            <div className="sub-tiku__practice-setting-type">
                {practiceQuestionNum.current.map(item => (
                    <div key={item}>{renderSettingOption(`${item}题`, 'practiceNum', item)}</div>
                ))}
            </div>
        </Modal>
    );
};

export default memo(PracticeSetting);
